import { BorderColorOutlined, Delete } from "@mui/icons-material";
import {
  Box,
  Divider,
  IconButton,
  Paper,
  Skeleton,
  SxProps,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React from "react";

export interface Column<T> {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  minWidth?: number;
  render?: (row: T, index: number) => React.ReactNode;
  format?: (value: any) => string | React.ReactNode;
}

interface DataTableProps<T> {
  data: T[];
  columns: Column<T>[];
  loading?: boolean;
  total?: number;
  page?: number;
  rowsPerPage?: number;
  onPageChange?: (event: unknown, newPage: number) => void;
  onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
  title?: string;
  showPagination?: boolean;
  onEdit?: (row: T, index: number) => void;
  onDelete?: (row: T, index: number) => void;
  onRowClick?: (row: T, index: number) => void;
  header?: React.ReactNode;
  stickyHeader?: boolean;
  isTotalRow?: boolean;
  stickyFooterRows?: (T & { isTotalRow?: boolean })[];
  renderExtraActions?: (row: T, index: number) => React.ReactNode;
  tableCellSx?: SxProps;
}

function DataTable<T extends Record<string, any>>({
  data,
  columns,
  loading = false,
  total = 0,
  page = 0,
  rowsPerPage = 10,
  onPageChange,
  onRowsPerPageChange,
  title,
  showPagination = false,
  onEdit,
  onDelete,
  onRowClick,
  stickyHeader = true,
  header,
  stickyFooterRows = [],
  renderExtraActions,
  tableCellSx,
}: DataTableProps<T>) {
  const rowsToShow = loading ? Array(rowsPerPage).fill({} as T) : data;

  return (
    <Paper elevation={3} sx={{ borderRadius: 2, overflow: "hidden" }}>
      {header ? (
        <Box sx={{ borderBottom: 1, borderColor: "divider" }}>{header}</Box>
      ) : title ? (
        <Box
          p={2}
          bgcolor="grey.100"
          sx={{ borderBottom: 1, borderColor: "divider" }}
        >
          <Typography variant="h6" fontWeight="bold">
            {title}
          </Typography>
        </Box>
      ) : null}

      <TableContainer sx={{ maxHeight: 600 }}>
        <Table stickyHeader={stickyHeader}>
          <TableHead>
            <TableRow>
              {columns.map((col, index) => (
                <TableCell
                  key={String(col.key)}
                  align={col.align || "left"}
                  sx={{ minWidth: col.minWidth, fontWeight: "bold" }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {rowsToShow.length === 0 && !loading ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  align="center"
                  sx={{ ...tableCellSx, py: 6 }}
                >
                  <Typography color="text.secondary">No data found</Typography>
                </TableCell>
              </TableRow>
            ) : (
              <>
                {/* Regular rows */}
                {rowsToShow.map((row, index) => (
                  <TableRow
                    key={row._id || index}
                    hover
                    onClick={() => onRowClick?.(row, index)}
                    sx={{ cursor: onRowClick ? "pointer" : "default" }}
                  >
                    {columns.map((col, index) => {
                      const isAction = col.key === "actions";

                      return (
                        <TableCell
                          key={String(col.key)}
                          align={col.align}
                          sx={{
                            ...tableCellSx,
                          }}
                        >
                          {loading ? (
                            <Skeleton variant="text" width="80%" />
                          ) : isAction ? (
                            <Box
                              sx={{
                                display: "flex",
                                gap: 1,
                                justifyContent:
                                  col.align === "right"
                                    ? "flex-end"
                                    : col.align === "center"
                                      ? "center"
                                      : "flex-start",
                                width: "100%",
                              }}
                            >
                              {onEdit && (
                                <IconButton
                                  size="medium"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onEdit(row, index);
                                  }}
                                >
                                  <BorderColorOutlined
                                    fontSize="medium"
                                    color="primary"
                                  />
                                </IconButton>
                              )}
                              {onDelete && (
                                <IconButton
                                  size="medium"
                                  color="error"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    onDelete(row, index);
                                  }}
                                >
                                  <Delete fontSize="medium" />
                                </IconButton>
                              )}
                              {renderExtraActions &&
                                renderExtraActions(row, index)}
                            </Box>
                          ) : col.render ? (
                            col.render(row, index)
                          ) : col.format ? (
                            col.format(row[col.key])
                          ) : (
                            (row[col.key] ?? "-")
                          )}
                        </TableCell>
                      );
                    })}
                  </TableRow>
                ))}
                {stickyFooterRows.map((footerRow, footerIndex) => (
                  <TableRow
                    key={`sticky-footer-${footerIndex}`}
                    sx={{
                      position: "sticky",
                      bottom: footerIndex * 0,
                      backgroundColor: "background.paper",
                      zIndex: 10,
                      borderTop: "2px solid",
                      borderColor: "divider",
                      "& td": {
                        fontWeight: "bold",
                        backgroundColor: "grey.50",
                      },
                    }}
                  >
                    {columns.map((col) => (
                      <TableCell
                        key={String(col.key)}
                        align={col.align || "left"}
                        sx={{
                          fontWeight: "bold",
                          py: 1.5,
                        }}
                      >
                        {col.render
                          ? col.render(footerRow, -1) // Use -1 or special index if needed
                          : col.format
                            ? col.format(footerRow[col.key])
                            : (footerRow[col.key] ?? "")}
                      </TableCell>
                    ))}
                  </TableRow>
                ))}
              </>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {showPagination && (
        <>
          <Divider />
          <TablePagination
            component="div"
            count={total || data.length}
            page={page}
            rowsPerPage={rowsPerPage}
            onPageChange={onPageChange!}
            onRowsPerPageChange={onRowsPerPageChange}
            rowsPerPageOptions={[5, 15, 10, 25, 50]}
          />
        </>
      )}
    </Paper>
  );
}

export default React.memo(DataTable) as typeof DataTable;
